import { NotificationSubscription, SubscriptionLevel } from "./types";

/**
 * Pure helpers over the one cached subscriptions list. Type-only imports on
 * purpose, so this file and its test never pull in axios.
 */

export interface SubscriptionEntity {
  topic: string;
  entityKind: string;
  entityKey: string;
  /** The stable identity the SERVER resolved (a ticker's company CIK), or null. */
  entityRef: string | null;
}

/**
 * The subscription that follows this entity. Uniqueness follows identity, the
 * same way the server's does:
 *
 * - with an `entityRef`, ONLY the identity is matched — so GOOGL reads "Alerts
 *   on" for a user who subscribed on GOOG, and a reused ticker label can never
 *   match the company that used to own it. `entityKey` is not consulted;
 * - without one, the key is matched, and only against subscriptions that have
 *   no identity either (a ticker subscription always has one).
 */
export const findSubscriptionForEntity = (
  subscriptions: NotificationSubscription[],
  { topic, entityKind, entityKey, entityRef }: SubscriptionEntity,
): NotificationSubscription | undefined =>
  subscriptions.find((subscription) => {
    if (subscription.topic !== topic || subscription.entityKind !== entityKind) return false;
    if (entityRef) return subscription.entityRef === entityRef;
    return subscription.entityRef === null && subscription.entityKey === entityKey;
  });

export interface SubscriptionGroup {
  topic: string;
  topicLabel: string;
  subscriptions: NotificationSubscription[];
}

const FALLBACK_TOPIC_LABEL = "Alerts";

/**
 * Grouped by topic in first-seen order; a topic this build has never heard of
 * groups like any other, under whatever label the server sent. An optimistic
 * row carries no label yet, so the heading comes from the first row that has one.
 */
export const groupSubscriptionsByTopic = (
  subscriptions: NotificationSubscription[],
): SubscriptionGroup[] => {
  const groups = new Map<string, SubscriptionGroup>();
  subscriptions.forEach((subscription) => {
    const group = groups.get(subscription.topic) ?? {
      topic: subscription.topic,
      topicLabel: FALLBACK_TOPIC_LABEL,
      subscriptions: [],
    };
    const hasOwnLabel = subscription.topicLabel.trim().length > 0;
    if (group.topicLabel === FALLBACK_TOPIC_LABEL && hasOwnLabel) {
      group.topicLabel = subscription.topicLabel;
    }
    group.subscriptions.push(subscription);
    groups.set(subscription.topic, group);
  });
  return Array.from(groups.values());
};

interface LevelOption {
  level: SubscriptionLevel;
  label: string;
  /** Secondary line under the option in the level dialog. */
  detail?: string;
}

const INSIDER_LEVEL_OPTIONS: LevelOption[] = [
  { level: "notable", label: "Buys and sells only", detail: "Open-market purchases and sales" },
  { level: "all", label: "All activity", detail: "Every filing, awards and exercises included" },
];

const GENERIC_LEVEL_OPTIONS: LevelOption[] = [
  { level: "notable", label: "Notable only" },
  { level: "all", label: "All activity" },
];

/** The default level first. An unknown topic gets generic wording. */
export const levelOptionsForTopic = (topic: string): LevelOption[] =>
  topic === "insider_trades" ? INSIDER_LEVEL_OPTIONS : GENERIC_LEVEL_OPTIONS;

/** The line under "Alert me about": what the alert follows. */
export const alertSubjectLine = (topic: string, subject: string): string =>
  topic === "insider_trades" ? `Insider trades at ${subject}` : subject;

export const levelLabelForTopic = (topic: string, level: SubscriptionLevel): string =>
  levelOptionsForTopic(topic).find((option) => option.level === level)?.label ?? "All activity";

const SUBSCRIBE_ERROR_MESSAGES = new Map<string, string>([
  ["INVALID_TOPIC", "Alerts aren't available for this yet."],
  ["INVALID_ENTITY", "Alerts aren't available for this company."],
  ["INVALID_LEVEL", "That alert setting isn't available. Please try again."],
  ["SUBSCRIPTION_LIMIT", "You've reached the limit of 200 alerts. Turn one off to add another."],
  ["MEMBERSHIP_REQUIRED", "Alerts are available to members."],
  ["PRO_REQUIRED", "Alerts are part of Insight Pro."],
  ["NOT_FOLLOWABLE", "Alerts aren't available for this company."],
  ["NO_INSIDER_DATA", "There's no insider data for this company yet, so there's nothing to alert on."],
  ["IDENTITY_UNAVAILABLE", "We couldn't set up this alert just now. Please try again."],
]);

export const SUBSCRIBE_FALLBACK_ERROR = "Couldn't update your alerts. Please try again.";

/** A readable sentence for each error code `POST /notification-subscriptions` returns. */
export const subscribeErrorMessage = (code: unknown): string =>
  SUBSCRIBE_ERROR_MESSAGES.get(typeof code === "string" ? code : "") ?? SUBSCRIBE_FALLBACK_ERROR;
